import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import axiosInstance from '../services/axiosInstance';
import GlobalTable from '../components/GlobalTable';
import { useLazyTable } from '../hooks/useLazyTable';
import CustomerDialog from '../components/CreateCustomerDialog';
import { validatePhoneNumber } from '../components/utils';
import { format } from 'date-fns';
import { Button } from 'primereact/button';
import { handleApiError, handleApiSuccess } from '../components/apiErrorHandler';
import { BatchAllocationDialog } from '../components/BatchAllocationDialog';
import InlineBatchEditorDialog from '../components/InlineBatchEditorDialog';
import PrescriptionDialog from '../components/CreatePrescriptionDialog';
import { confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import OperatorAuthDialog from '../components/OperatorAuthDialog';
import UploadFilePanel from "../components/UploadFilePanel";
import { usePermission } from '../contexts/UserContext';

const Prescriptions = () => {
    const [prescriptions, setPrescriptions] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const { lazyParams, onPage, onSort, onFilter, queryParams } = useLazyTable({ rows: 15 });
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const location = useLocation();
    const { prescriptionId, customerId, refillItems, refillRequestId, refillRequestDays, clinicPrescriptionMode, clinicSourceName } = location.state || {};
    const { push, pop, beginDrilldown } = useBreadcrumbs();
    const [prescription, setPrescription] = useState({
        customer: null,
        doctor_name: '',
        date: new Date(),
        items: [{ product: null, quantity: '', dosage: '1', frequency: '', duration: '' }],
    });
    const [dialogVisible, setDialogVisible] = useState(false);
    const [loading, setLoading] = useState(true);
    const [dialogName, setDialogName] = useState('Create Prescription');
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [isRevising, setIsRevising] = useState(false); 
    const [isRefillMode, setIsRefillMode] = useState(false);
    const [refillDaysCapture, setRefillDaysCapture] = useState(null);
    const [isClinicMode, setIsClinicMode] = useState(false);
    const [clinicSourceCapture, setClinicSourceCapture] = useState(null);
    const isRefillRequest = !!(refillItems && refillItems.length);
    const frequency = [
        {label: "BD", value: "BD"},
        {label: "TDS", value: "TDS"},
        {label: "QDS", value: "QDS"},
        {label: "Mane", value: "Mane"},
        {label: "Nocte", value: "Nocte"},
        {label: "QHS", value: "QHS"},
        {label: "Q4H", value: "Q4H"},
        {label: "Q6H", value: "Q6H"},
        {label: "Q8H", value: "Q8H"},
        {label: "Q12H", value: "Q12H"},
        {label: "EOD", value: "EOD"},
        {label: "STAT", value: "STAT"},
        {label: "SOS", value: "SOS"},
        {label: "PRN", value: "PRN"},
        {label: "Other", value: "Other"},
    ];
    const [isCustomerDialogVisible, setCustomerDialogVisible] = useState(false);
    const [customer, setCustomer] = useState({
            title: "",
            gender: "",
            first_name: "",
            last_name: "",
            email: null,
            phone_number: null,
            address: "",
            nic: "",
            passport: "",
            dob: null,
            category: "",
            segment: "",
            loyalty: "",
            priority: 5,
            status: "Regular",
            organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
            relationship: "",
            relative: null,
            relative_flg: false,
        });
    const titles = [
        { label: "Mr.", value: "Mr." },
        { label: "Mss.", value: "Mss." },
        { label: "Mrs.", value: "Mrs." },
        { label: "Dr.", value: "Dr." },
        { label: "Ven.", value: "Ven." },
        { label: "Rev.", value: "Rev." },
    ];
    const segments = [
        { label: "High Revenue", value: "High Revenue" },
        { label: "Medium Revenue", value: "Medium Revenue" },
        { label: "Low Revenue", value: "Low Revenue" },
        { label: "Negative Revenue", value: "Negative Revenue" },
        ];
    const categories = [
        {label: "Store visitors", value: "Store visitors" },
        {label: "Home delivery", value: "Home delivery" },
        {label: "Convenience", value: "Convenience" },
        {label: "Loyal", value: "Loyal" },
    ];
    const priority = [
        {label: 1, value: 1 },
        {label: 2, value: 2 },
        {label: 3, value: 3 },
        {label: 4, value: 4 },
        {label: 5, value: 5 },
    ];
    const status = [
        {label: "Regular", value: "Regular" },
        {label: "Blacklisted", value: "Blacklisted" },
        {label: "Inactive", value: "Inactive" },
    ];
    const isAdminUser = () => {
        const userSettings = JSON.parse(localStorage.getItem('userSettings') || '{}');
        return userSettings?.isadmin === true;
    };
    const prodInputRefs = useRef([]);
    const [pendingBatchAllocations, setPendingBatchAllocations] = useState([]);
    const [pendingBatchDialog, setPendingBatchDialog] = useState(null);
    const [convertedInvoiceItems, setConvertedInvoiceItems] = useState([]);
    const pendingPrescriptionId = useRef(null);
    const [showInlineBatchEditor, setShowInlineBatchEditor] = useState(false);
    const [inlineBatchData, setInlineBatchData] = useState([]);
    const [inlineBatchAvailableItems, setInlineBatchAvailableItems] = useState([]);
    const [tempFinalInvoiceItems, setTempFinalInvoiceItems] = useState([]);
    const [tempItemsToConfirm, setTempItemsToConfirm] = useState([]);
    const [confirmNavActive, setConfirmNavActive] = useState(false);
    // Exceptional (prescription→invoice) inventory flow
    const [exceptionalVisible, setExceptionalVisible] = useState(false);
    const [exceptionalQueue, setExceptionalQueue] = useState([]);
    const [allowExceptional, setAllowExceptional] = useState(true);
    const [exceptionalForm, setExceptionalForm] = useState({
        itemId: null,
        productObj: null,
        productId: null,
        qty: 0,
        expiry: null,
        purchase_price: 0,
        selling_price: 0,
        batch_number: '',
        useExisting: false,         
        deactivateExisting: false,
    });
    // ---- Exceptional dialog refs for keyboard flow ----
    const exExpiryRef   = useRef(null);   
    const exPurchaseRef = useRef(null);  
    const exSellingRef  = useRef(null);  
    const exAddBtnRef   = useRef(null);  
    const askNextRef = useRef(null);           
    const pendingChoiceQueueRef = useRef([]);
    const pendingOutOfStockWithoutActiveRef = useRef(null);
    const [exceptionalByProduct, setExceptionalByProduct] = useState({});
    const [exceptionalActiveInfo, setExceptionalActiveInfo] = useState(null);
    const exceptionalByProductRef = useRef({});
    const handleRevise = () => {
        if (!canManagePrescriptions) return;
        setIsReadOnly(false); 
        setIsRevising(true);  
    };
    const verifiedOperatorRef = useRef(null);
    const money = (v) => Number(Number(v || 0).toFixed(2));
    const lineTotal = (qty, price) => money(money(qty) * money(price));
    const [showOperatorAuth, setShowOperatorAuth] = useState(false);
    const [pendingOperatorAction, setPendingOperatorAction] = useState(null);
    const [verifiedOperator, setVerifiedOperator] = useState(null);
    const [operatorAuthEnabled, setOperatorAuthEnabled] = useState(false);
    const [pendingConvertAction, setPendingConvertAction] = useState(null);
    const canManagePrescriptions = usePermission('manage_prescriptions');
    const canManageCustomers     = usePermission('manage_customers');
    const canManageInvoice     = usePermission('create_invoices');

    const portalRefillRequestId      = useRef(location.state?.refillRequestId || null);
    const portalPrescriptionUploadId = useRef(location.state?.portalPrescriptionUploadId || null);
    // Upload panel for existing prescriptions that have a linked portal upload
    const [prescriptionUploadData, setPrescriptionUploadData] = useState(null);

    useEffect(() => { exceptionalByProductRef.current = exceptionalByProduct; }, [exceptionalByProduct]);
    // Promise-based confirm for linear async flows
        const confirmAsync = (opts) =>
        new Promise((resolve) => {
            confirmDialog({
            ...opts,
            accept: () => resolve('accept'),
            reject: () => resolve('reject'),
            onShow: () => setConfirmNavActive(true),
            onHide: () => setConfirmNavActive(false),
            });
    });

    useEffect(() => {
        const loadPrescriptionFromNav = async () => {
            if (!location.state?.prescriptionId) return;

            try {
                const prescriptionToOpen = await axiosInstance.get(`/api/prescriptions/${location.state.prescriptionId}/`);
                if (prescriptionToOpen.data) {
                    let loadedPrescription = prescriptionToOpen.data;

                    // clinic_source_name is set server-side (see
                    // DetailedPrescriptionSerializer.get_clinic_source_name)
                    // whenever this Prescription came from accept_clinic_
                    // prescription — a persisted fact, not just the one-time
                    // router state from the Accept click. This is what makes
                    // the quantity-entry gate reapply correctly if a
                    // pharmacist opens this dialog, hits Cancel without
                    // finishing, and reopens it later from the plain
                    // Prescriptions list — otherwise those items would stay
                    // stuck at quantity 0 forever (silently dropped by
                    // batchAllocatePrescription's `quantity > 0` filter,
                    // blocking conversion) with no way back into edit mode.
                    const clinicSource = clinicSourceName ?? loadedPrescription.clinic_source_name ?? null;
                    const isClinicSourced = clinicPrescriptionMode || !!loadedPrescription.clinic_source_name;
                    if (isClinicSourced) {
                        // Doctor never enters a fill quantity (see accept_
                        // clinic_prescription's docstring) — every item still
                        // at 0 needs it set by the pharmacist here, same gate
                        // as refill mode below. Items already given a real
                        // quantity on an earlier pass are left untouched so
                        // partial progress isn't lost.
                        loadedPrescription.items = loadedPrescription.items.map(item => ({
                            ...item,
                            quantity: Number(item.quantity) > 0 ? item.quantity : null,
                        }));
                    }

                    if (refillItems && refillItems.length > 0) {
                        const requestedProducts = refillItems.map(i => Number(i.product_id));
                        const requestedDays = Number(refillRequestDays) || 0;
                
                        // Filter to only the medicines the patient requested
                        loadedPrescription.items = loadedPrescription.items
                            .filter(item => {
                                const pid = item.product?.id ?? item.product;
                                return requestedProducts.includes(Number(pid));
                            })
                            .map(item => {
                                const pid = item.product?.id ?? item.product;
                                const match = refillItems.find(r => Number(r.product_id) === Number(pid));
                                const itemDuration  = Number(item.duration) || 0;
                                const durationMatch = requestedDays > 0 && itemDuration === requestedDays;
                
                                return {
                                    ...item,
                                    // Always update item duration to match requested days
                                    duration:       requestedDays > 0 ? requestedDays : item.duration,
                                    quantity:       durationMatch ? item.quantity : null,
                                    is_refill_item: !!match,
                                };
                            });
                
                        // Also update the prescription-level duration to match requested days
                        if (requestedDays > 0) {
                            loadedPrescription.duration = requestedDays;
                        }
                    }
                    setPrescription(loadedPrescription);
                    setIsReadOnly(true); // Enable read-only mode
                    setDialogVisible(true);
                    setDialogName(`Prescription Details: ${prescriptionToOpen.data.id}`);
                    if (refillItems && refillItems.length > 0) {
                        setIsRefillMode(true);
                        setRefillDaysCapture(refillRequestDays ?? null);
                    }
                    if (isClinicSourced) {
                        setIsClinicMode(true);
                        setClinicSourceCapture(clinicSource);
                    }
                    navigate(location.pathname, { replace: true, state: {} });
                }
            } catch (err) {
                handleApiError(err);
            }
        };

        loadPrescriptionFromNav();
    }, [location.state?.prescriptionId]);

    useEffect(() => {
        const cached = localStorage.getItem('organizationSettings');
        if (!cached) return;

        try {
            const parsed = JSON.parse(cached);
            const cfg = parsed?.custom_json_settings?.operator_authentication;
            const enabled =
                cfg?.enabled === true &&
                Array.isArray(cfg?.methods) &&
                cfg.methods.length > 0;
            setOperatorAuthEnabled(enabled);
        } catch {
            setOperatorAuthEnabled(false);
        }
    }, []);

       // Merge duplicates by (rx_item_id|product|batch_number|inventory) and SUM quantities
    const combineInvoiceLines = (arr = []) => {
        const map = new Map();
        const passthrough = [];
        const normPid = (p) => String((p && p.id != null) ? p.id : p ?? '');
        const normBatch = (b) => String(b ?? '').trim().toUpperCase();

        for (const li of (arr || [])) {
            const pid = normPid(li?.product);
            const bn  = normBatch(li?.batch_number);

            // don’t merge lines without a concrete batch number
            if (!bn) { passthrough.push({ ...li }); continue; }

            const key = `${pid}|${bn}`;
            if (map.has(key)) {
                const prev = map.get(key);
                const qty  = (Number(prev.quantity) || 0) + (Number(li.quantity) || 0);
                const price = Number(prev.price ?? li.price ?? 0);
                map.set(key, { ...prev, quantity: qty, total: lineTotal(qty, price) });
            } else {
                const price = Number(li.price ?? 0);
                map.set(key, { ...li, total: (Number(li.quantity) || 0) * price });
            }
        }
        return [...Array.from(map.values()), ...passthrough];
    };

    // Centralized runner for NEW vs OLD decisions (used in main + resume paths)
    const runChoiceQueue = async (queue, finalInvoiceSnapshot = [], toConfirmSnapshot = []) => {
      let finalInvoiceItems = [...finalInvoiceSnapshot];
      let itemsToConfirm = [...toConfirmSnapshot];

      const inactivateExceptionalViaId = async (batchId) => {
        try { await axiosInstance.post('/api/inventory/exceptional/inactivate/', { batch_id: batchId }); }
        catch (e) { handleApiError(e); }
      };

      while (queue.length) {
        const current = queue[0];
        const pid = current.item.product.id || current.item.product;
        const exMap = exceptionalByProductRef.current || {};
        const ex  = exMap[pid];
        const name = current.item.product?.name || `#${pid}`;
		const batches = current.allocations || [];
        const requestedQty = Number(current.item.quantity || 0);
        const allocatedQty = batches.reduce((s, a) => s + (Number(a.allocated_quantity) || 0), 0);
        console.log("Queue : ", queue);
        console.log("ExAll: ", exMap);
        console.log("pid: ",pid);
        // --- NEW GUARD: if ALL available batches are exceptional, skip NEW/OLD confirm entirely ---
        // We treat these as pure-exceptional allocations:
        //  - If they fully cover the request, add them directly.
        //  - If they are short, add what's there and immediately open exceptional to top-up the remainder.
        if (batches.length > 0 && allBatchesMarkedExceptional(batches, ex)) {
          // 1) Add allocated exceptional batches directly
          for (const a of batches) {
            const productObj = current.item.product;
            const line = {
              id: Date.now() + Math.random(),
              product: productObj,
              product_id: productObj?.id ?? current.item.product,
              rx_item_id: current.item.id,
              quantity: Number(a.allocated_quantity) || 0,
              price: Number(a.selling_price) || 0,
              batch_number: a.batch_number,
              expiry_date: a.expiry_date,
              price_list: a.price_list_pk,
              inventory: a.inventory_id,
            };
            finalInvoiceItems.push(line);
            setTempFinalInvoiceItems(prev => uniqueInvoiceLines([...(prev || []), line]));
          }

          // 2) If short, top-up remainder via exceptional dialog (existing vs new)
          const remainder = Math.max(0, requestedQty - allocatedQty);
          if (remainder > 0) {
            console.log("Inside choice queue 1", ex);
            // Park the rest of the queue, then open exceptional to add the remainder
            pendingChoiceQueueRef.current = queue.slice(1);
            setTempItemsToConfirm(prev => {
              const add = Array.isArray(itemsToConfirm) ? itemsToConfirm : [];
              return Array.isArray(prev) ? [...prev, ...add] : add;
            });
            setExceptionalQueue([{ item: current.item, qty: remainder }]);
            openExceptionalDialog(ex?.active ? 'existing' : 'new', { item: current.item, qty: remainder }, ex || null);
            return; // will resume after exceptional completes
          }

          // Fully covered by exceptional: advance the queue without any confirm
          queue = queue.slice(1);
          continue;
        }
        const result = await confirmAsync({
          header: `Choose stock source — ${name}`,
          message: 'Both NEW batches and OLD (exceptional) stock exist. Which do you want to use?',
          acceptLabel: 'Use OLD (exceptional)',
          rejectLabel: 'Use NEW (allocated)',
        });

        if (result === 'accept') {
          // Use OLD: open exceptional for remainder (or full if you prefer OLD entirely)
          const allocatedQty = (current.allocations || []).reduce((s, a) => s + (Number(a.allocated_quantity) || 0), 0);
          const requestedQty = Number(current.item.quantity || 0);
          const remainder = Math.max(0, requestedQty - allocatedQty) || requestedQty;

          // Park the rest of the queue and open the exceptional dialog
          pendingChoiceQueueRef.current = queue.slice(1);
          setTempFinalInvoiceItems(prev => uniqueInvoiceLines([...(prev || []), ...finalInvoiceItems]));
          setTempItemsToConfirm(prev => {
            const add = Array.isArray(itemsToConfirm) ? itemsToConfirm : [];
            return Array.isArray(prev) ? uniqueInvoiceLines([...(prev || []), ...add]) : add;
          });
          setExceptionalQueue([{ item: current.item, qty: remainder }]);
          openExceptionalDialog('existing', { item: current.item, qty: remainder }, ex);
          return; // resume after exceptional completes
        } else {
          // Use NEW
          if (ex?.active && ex.inventory_id) {
            await inactivateExceptionalViaId(ex.inventory_id);
            setExceptionalByProduct(prev => ({
              ...prev,
              [pid]: { ...(prev?.[pid] || {}), active: false }
            }));
          }

          const requestedQty = Number(current.item.quantity || 0);
          const allocatedQty = (current.allocations || []).reduce((s, a) => s + (Number(a.allocated_quantity) || 0), 0);

          if (current.type === 'single' && current.singleLine) {
            finalInvoiceItems.push(current.singleLine);
            setTempFinalInvoiceItems(prev => uniqueInvoiceLines([...(prev || []), current.singleLine]));
            setConvertedInvoiceItems(prev => {
              const next = uniqueInvoiceLines([...(prev || []), current.singleLine]);
              convertedInvoiceItemsRef.current = next;
              return next;
            });
          } else if ((current.allocations || []).length === 1) {
            const a = current.allocations[0];
            if (allocatedQty < requestedQty) {
              // Single-batch PARTIAL NEW → deferred confirm
              await new Promise((r) => setTimeout(r, 0));
              const r2 = await confirmAsync({
                header: `Partial stock — ${name}`,
                message: `Only ${allocatedQty} of ${requestedQty} is available from NEW stock.\nAdd the available quantity to the invoice?`,
                acceptLabel: 'Add available',
                rejectLabel: 'Skip',
              });
              if (r2 === 'accept') {
                const productObj = current.item.product;
                const line = {
                  id: Date.now() + Math.random(),
                  product: productObj,
                  product_id: productObj?.id ?? current.item.product,
                  rx_item_id: current.item.id,
                  quantity: Number(a.allocated_quantity) || 0,
                  price: Number(a.selling_price) || 0,
                  batch_number: a.batch_number,
                  expiry_date: a.expiry_date,
                  price_list: a.price_list_pk,
                  inventory: a.inventory_id,
                };
                finalInvoiceItems.push(line);
                setTempFinalInvoiceItems(prev => uniqueInvoiceLines([...(prev || []), line]));
                setConvertedInvoiceItems(prev => {
                  const next = uniqueInvoiceLines([...(prev || []), line]);
                  convertedInvoiceItemsRef.current = next;
                  return next;
                });
              }
            } else {
              // Single-batch FULL NEW
              const productObj = current.item.product;
              const line = {
                id: Date.now() + Math.random(),
                product: productObj,
                product_id: productObj?.id ?? current.item.product,
                rx_item_id: current.item.id,
                quantity: Number(a.allocated_quantity) || 0,
                price: Number(a.selling_price) || 0,
                batch_number: a.batch_number,
                expiry_date: a.expiry_date,
                price_list: a.price_list_pk,
                inventory: a.inventory_id,
              };
              finalInvoiceItems.push(line);
              setTempFinalInvoiceItems(prev => uniqueInvoiceLines([...(prev || []), line]));
              setConvertedInvoiceItems(prev => {
                const next = uniqueInvoiceLines([...(prev || []), line]);
                convertedInvoiceItemsRef.current = next;
                return next;
              });
            }
          } else {
            // Multi-batch NEW → go to split editor (filter OUT exceptional batches)
            const nonExceptional = (current.allocations || []).filter(b => {
              const isExc = (typeof b.exceptional !== 'undefined') ? !!b.exceptional : (ex?.inventory_id && b.inventory_id === ex.inventory_id);
              return !isExc;
            });
            itemsToConfirm.push({ item: current.item, allocations: nonExceptional });
            setTempItemsToConfirm(prev =>
              (Array.isArray(prev) ? [...prev, { item: current.item, allocations: nonExceptional }] : [{ item: current.item, allocations: nonExceptional }])
            );
          }
          queue = queue.slice(1);
        }
      }

      // Finished the choice queue — merge and continue
      const combined = combineInvoiceLines([
        ...(convertedInvoiceItemsRef.current || []),
        ...(tempFinalInvoiceItems || []),
        ...finalInvoiceItems,
      ]);
      setConvertedInvoiceItems(combined);
      convertedInvoiceItemsRef.current = combined;

      if (itemsToConfirm.length > 0 || (Array.isArray(tempItemsToConfirm) && tempItemsToConfirm.length > 0)) {
        setInlineBatchData(itemsToConfirm);
        setInlineBatchAvailableItems(combined);
        setShowInlineBatchEditor(true);
      } else {
        handleFinalInvoice(combined);
      }
    };

    const confirmWithNav = (opts) => {
        confirmDialog({
            ...opts,
            onShow: () => setConfirmNavActive(true),
            onHide: () => setConfirmNavActive(false),
        });
    };

    const handleCustomerDialogVisible = () => {
        setCustomerDialogVisible(true);
    }
    const convertedInvoiceItemsRef = useRef([]);

    useEffect(() => { convertedInvoiceItemsRef.current = convertedInvoiceItems; }, [convertedInvoiceItems]);

    useEffect(() => {
        const onKey = (e) => {
            if (!confirmNavActive) return;

            // Find the currently open PrimeReact ConfirmDialog
            const dlg = document.querySelector('.p-confirm-dialog');
            if (!dlg) return;
            const footer = dlg.querySelector('.p-dialog-footer');
            if (!footer) return;
            const buttons = Array.from(footer.querySelectorAll('button'));
            if (buttons.length === 0) return;

            const focusedIdx = buttons.findIndex((b) => b === document.activeElement);

            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                e.preventDefault();
                if (focusedIdx === -1) {
                    buttons[0].focus(); // start on first
                    return;
            }
                const delta = e.key === 'ArrowRight' ? 1 : -1;
                const next = (focusedIdx + delta + buttons.length) % buttons.length;
                buttons[next].focus();
            } else if (e.key === 'Enter' && focusedIdx >= 0) {
                e.preventDefault();
                buttons[focusedIdx].click();
            }
        };

        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [confirmNavActive]);

    // load org settings for exceptional flag (re-use localStorage if available)
    useEffect(() => {
        const cached = localStorage.getItem('organizationSettings');
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                const exceptionalAllowed = typeof parsed.allow_exceptional_inventory !== 'undefined'
                    ? parsed.allow_exceptional_inventory
                    : true;
                setAllowExceptional(exceptionalAllowed);
            } catch (e) {
                setAllowExceptional(true);
            }
        } else {
            // best-effort fetch (non-blocking)
            (async () => {
                try {
                    const r = await axiosInstance.get('/api/orgsettings/');
                    const exceptionalAllowed = typeof r.data.allow_exceptional_inventory !== 'undefined'
                        ? r.data.allow_exceptional_inventory
                        : true;
                    setAllowExceptional(exceptionalAllowed);
                    localStorage.setItem('organizationSettings', JSON.stringify(r.data));
                } catch (e) {
                    // ignore; default true
                    setAllowExceptional(true);
                }
            })();
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                if (customerId) {
                    await fetchPrescriptions(customerId);
                } else {
                const prescriptionResult = await axiosInstance.get('/api/prescriptions/', { params: queryParams });
                setPrescriptions(prescriptionResult.data.results);
                setTotalRecords(prescriptionResult.data.count);
                setLoading(false);
                }
            } catch (error) {
                handleApiError(error);
                setLoading(false);
            }
            finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [customerId, queryParams]);

    useEffect(() => {
        if (location.state?.customer) {
            const raw  = location.state.customer;
            // Normalise {id, name} shape from PrescriptionUploads
            const cust = raw.first_name !== undefined ? raw : {
                ...raw,
                first_name: raw.name?.split(' ')[0] || '',
                last_name:  raw.name?.split(' ').slice(1).join(' ') || '',
            };
            const displayLabel = cust.displayLabel
                || raw.name
                || `${cust.first_name||''} ${cust.last_name||''}`.trim();
            const custWithLabel = { ...cust, displayLabel };
            setPrescription(prev => ({ ...prev, customer: custWithLabel }));
            setDialogVisible(true);
    } else if (location.state?.openCreate) {
            setDialogVisible(true); 
        }
    }, [location.state?.customer]);
    
    useEffect(() => {
        if (customerId) {
            fetchPrescriptions(customerId);
        }
    }, [customerId]);

    const uniqueInvoiceLines = (arr = []) => {
        const seen = new Set();
        const out = [];
        for (const li of (arr || [])) {
            const rx = li?.rx_item_id ?? li?.id ?? '';
            const pid = li?.product?.id ?? li?.product;
            const bn  = li?.batch_number ?? '';
            const inv = li?.inventory ?? '';
            const key = `${rx}|${pid}|${bn}|${inv}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(li);
        }
        return out;
    };
    // Helper: are all returned batches effectively "exceptional"?
    const allBatchesMarkedExceptional = (batches = [], activeExceptional) => {
        if (!batches || !batches.length) return false;
        const allHaveFlag = batches.every(b => typeof b.exceptional !== 'undefined');
        if (allHaveFlag) {
            return batches.every(b => !!b.exceptional);
        }
        if (activeExceptional && activeExceptional.inventory_id) {
            return batches.every(b => b.inventory_id === activeExceptional.inventory_id);
        }
        return false;
    };

    const openExceptionalDialog = async (a, b, c) => {
        let mode, ctx, activeInfo;
        if (typeof a === 'string') {
            mode = a; ctx = b; activeInfo = c;
        } else {
            ctx = a;
            const pid = ctx.item.product.id || ctx.item.product;
            const ex  = (exceptionalByProductRef.current || {})[pid] || {};
            mode = ex?.active ? 'existing' : 'new';
            activeInfo = ex;
        }

        const isExisting = mode === 'existing';
        const pid = ctx.item.product.id || ctx.item.product;
        const ex  = activeInfo || {};
        setExceptionalActiveInfo(activeInfo || null);
        console.log("ProductObj:", ctx.item.product);

        let purchasePrice = 0;
        let sellingPrice  = 0;

        if (isExisting) {
            purchasePrice = (ex.purchase_price ?? 0) >= 0 ? ex.purchase_price : 0;
            sellingPrice  = ex.selling_price ?? 0;
        } else {
            try {
                const res = await axiosInstance.get(
                    `/api/products/${pid}/active-pricelist/`
                );
                purchasePrice = res.data?.purchase_price ?? 0;
                sellingPrice  = res.data?.selling_price ?? Number(ctx.item.price || 0);
            } catch (err) {
                console.warn("Failed to fetch active pricelist, falling back to item price:", err);
                sellingPrice = Number(ctx.item.price || 0);
            }
        }

        setExceptionalForm({
            itemId: ctx.item.id,
            productObj: ctx.item.product,
            productId: pid,
            qty: ctx.qty,
            expiry: isExisting && ex.expiry_date ? new Date(ex.expiry_date) : null,
            purchase_price: purchasePrice,
            selling_price: sellingPrice,
            batch_number: isExisting && ex.batch_number ? ex.batch_number : '',
            useExisting: isExisting,
            deactivateExisting: !isExisting && !!ex.active,
        });
        setExceptionalVisible(true);
    };

    const fetchPrescriptions = async (customerId) => {
        try {
            const response = await axiosInstance.get(`/api/prescriptions/active-for-customer/${customerId}/`);
            setPrescriptions(response.data);

            // Automatically open the dialog if prescriptionId exists
            if (prescriptionId) {
                const prescriptionToOpen = await axiosInstance.get(`/api/prescriptions/${prescriptionId}/`);
                if (prescriptionToOpen.data) {
                    setPrescription(prescriptionToOpen.data);
                    setIsReadOnly(true); // Enable read-only mode
                    setDialogVisible(true);
                    setDialogName(`Prescription Details: ${prescriptionToOpen.data.id}`);
                }
            }
        } catch (error) {
            handleApiError(error);
        }
    };
        // Active exceptional for prefill
    const fetchActiveExceptional = async (productId) => {
        try {
            const res = await axiosInstance.get('/api/inventory/exceptional/active/', {
            params: { product_id: productId },
            });
            return res?.data?.active ? res.data : null;
        } catch (e) {
            // silent — we’ll just fall back to item price
            return null;
        }
    };

 // Deactivate existing exceptional if user proceeds with NEW batch
    const inactivateActiveExceptional = async (productId) => {
        try {
            const info = await fetchActiveExceptional(productId);
            if (!info || !info.inventory_id) return;
            await axiosInstance.post('/api/inventory/exceptional/inactivate/', { batch_id: info.inventory_id });
        } catch (e) {
            handleApiError(e);
        }
    };

    const handleSearchCustomer = (query) => {
        axiosInstance
                .get(`/api/customerq?query=${query}`)
                .then((res) => {
                    const processedSuggestions = res.data.map((cust) => ({
                        ...cust,
                        displayLabel: `${cust.first_name} ${cust.last_name}`,
                    }));
                    setFilteredCustomers(processedSuggestions);
                })
                .catch((error) => handleApiError(error));
    }

    const handleCusChange = (e, field) => {
        const value = e.target ? e.target.value : e.value;
        setCustomer((prev) => {
            const updatedCustomer = { ...prev, [field]: value };
    
            // Set default title based on gender
            if (field === "gender") {
                if (value === "M") {
                    updatedCustomer.title = "Mr.";
                } else if (value === "F") {
                    updatedCustomer.title = "Mss.";
                }
            }
    
            return updatedCustomer;
        });
    };

    const handleCusSubmit = async (e) => {
        e.preventDefault();
        
        const validatedPhoneNumber = validatePhoneNumber(customer.phone_number ? customer.phone_number : '0700000000');
        if (!validatedPhoneNumber) {
            handleApiError({ Error: "Invalid phone number. Please enter a valid number." });
            return;
        }
        const formattedCustomer = {
            ...customer,
            phone_number: customer.phone_number ? validatedPhoneNumber : null,
            dob: customer.dob ? format(new Date(customer.dob), 'yyyy-MM-dd') : null, 
        };
    
        // Ensure title matches gender selection
        if (
            (customer.gender === "M" && customer.title !== "Mr." && customer.title !== "Dr." && customer.title !== "Ven." && customer.title !== "Rev.") ||
            (customer.gender === "F" && customer.title !== "Mss." && customer.title !== "Mrs." && customer.title !== "Dr." && customer.title !== "Ven." && customer.title !== "Rev.")
        ) {
            handleApiError({ Error: "Invalid title for the selected gender" });
            return;
        }
        try {
            const result = await axiosInstance.post("/api/customer/", formattedCustomer);
            const newCustomer= result.data;
            const formattedNewCustomer = { ...newCustomer, displayLabel: `${newCustomer.first_name} ${newCustomer.last_name}` };
            setPrescription((prev) => ({ ...prev, customer: formattedNewCustomer }));
            setFilteredCustomers((prev) => [...prev, formattedNewCustomer]);
            setCustomer({
                title: "",
                gender: "",
                first_name: "",
                last_name: "",
                email: null,
                phone_number: null,
                address: "",
                nic: "",
                passport: "",
                dob: null,
                category: "",
                segment: "",
                loyalty: "",
                priority: 5,
                status: "Regular",
                organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
                relationship: "",
                relative: null,
                ralative_flg: false,
            });
            handleApiSuccess(`Customer ${result.data.first_name} ${result.data.last_name} saved successfully.`);
            setCustomerDialogVisible(false);
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleItemChange = (itemId, field, value) => {
        setPrescription((prev) => {
            const updatedItems = prev.items.map(item =>
                item.id === itemId ? { ...item, [field]: value } : item
            );
            let item = updatedItems.find(item => item.id === itemId);
    
            const frequencyMap = {
                BD: 2,
                TDS: 3,
                QDS: 4,
                Mane: 1,
                Nocte: 1,
                QHS: 1,
                Q4H: 6,
                Q6H: 4,
                Q8H: 3,
                Q12H:2,
                EOD: .5,
            };
    
            // Calculate quantity only if product.uses is Tablet or Capsule and frequency is valid
            if (
                (field === "dosage" || field === "duration" || field === "frequency") &&
                item?.product?.uses &&
                (item.product.uses === "Tablet" || item.product.uses === "Capsule")
            ) {
                const frequencyValue = frequencyMap[item.frequency] || null;
                if (frequencyValue && item.dosage && item.duration) {
                    item.quantity = item.dosage * item.duration * frequencyValue;
                    // Put the updated item back in the array
                    const idx = updatedItems.findIndex(i => i.id === itemId);
                    updatedItems[idx] = item;
                }
            }

            return { ...prev, items: updatedItems };
        });
    };
    
    const navigate = useNavigate(); 
    
    const requireOperatorThen = async (action) => {
        const orgSettings = JSON.parse(localStorage.getItem('organizationSettings') || '{}');
        const enabled = orgSettings?.operator_authentication?.enabled;

        if (!enabled) {
            await action(null);
            return;
        }

        setPendingOperatorAction(() => action);
        setShowOperatorAuth(true);
    };

    // Persists quantity/duration/the refill-reminder flag on the EXISTING
    // Prescription/PrescriptionItem rows in place — deliberately not the
    // full PUT/PATCH `/api/prescriptions/<id>/` path, which unconditionally
    // bumps version and destroys+recreates every item row (see
    // gf.views.PrescriptionViewSet.fill_details's docstring). Called by the
    // dialog's "Save" button while viewing an existing prescription (via
    // handleSubmit's isReadOnly branch below), and by handleConvertClick so
    // Convert always leaves the record itself correct, not just the
    // resulting invoice.
    const handleSaveFillDetails = async () => {
        try {
            const payload = {
                duration: prescription.duration,
                auto_create_refill_reminder: prescription.auto_create_refill_reminder !== false,
                items: prescription.items.map((item) => ({
                    id: item.id,
                    quantity: item.quantity,
                    duration: item.duration,
                })),
            };
            const result = await axiosInstance.patch(`/api/prescriptions/${prescription.id}/fill-details/`, payload);
            setPrescription(result.data);
            handleApiSuccess('Saved');
            return result.data;
        } catch (err) {
            handleApiError(err);
            return null;
        }
    };

    const handleConvertClick = async () => {
        // Quantity must be set before converting, for every prescription —
        // not just refill/clinic mode. batchAllocatePrescription silently
        // drops any item with quantity <= 0 (see its own `quantity > 0`
        // filter below), so leaving this unchecked doesn't error, it just
        // quietly converts fewer items than intended.
        const missing = prescription.items.filter(
            it => !it.quantity || Number(it.quantity) < 1
        );
        if (missing.length > 0) {
            const names = missing.map(it => it.product?.name || "Unknown").join(", ");
            alert(`Please set the quantity for: ${names}`);
            return;
        }

        const saved = await handleSaveFillDetails();
        if (!saved) return;

        if (operatorAuthEnabled && !verifiedOperatorRef.current) {
            // Close over `saved` explicitly (not the ambient `prescription`
            // state) — operator auth defers this call to a later render, by
            // which point `prescription` may not yet reflect the fill-details
            // save that just completed above.
            setPendingConvertAction(() => () => batchAllocatePrescription(saved.items));
            setShowOperatorAuth(true);
            return;
        }
        await batchAllocatePrescription(saved.items);
    };

    const addNewItem = () => {
        const newId = Date.now() + Math.random();
        setPrescription((prev) => ({
            ...prev,
            items: [
                {
                    id: newId,
                    product: null,
                    quantity: '',
                    dosage: '1',
                    frequency: '',
                    duration: prev.duration || '',
                }, ...prev.items,
            ],
        }));
        setTimeout(() => {
            prodInputRefs.current[newId]?.focus();
        }, 100);

    };

 const batchAllocatePrescription = async (prescriptionItems) => {
        const cleanedItems = (prescriptionItems || []).filter(it => it?.product && Number(it.quantity) > 0);
        const productsToAllocate = cleanedItems.map(item => ({
            product_id: item.product.id || item.product,
            quantity: Number(item.quantity) || 0,
        }));

        const res = await axiosInstance.post('/api/inventory/allocate-batches-bulk/', productsToAllocate);
        const allocationsByProduct = {};
        const exceptionalByProduct = {};
        res.data.allocations.forEach(a => {
            allocationsByProduct[a.product_id] = a;
            exceptionalByProduct[a.product_id] = a.exceptional || { active: false };
        });
        setExceptionalByProduct(exceptionalByProduct);
        exceptionalByProductRef.current = exceptionalByProduct;

        let finalInvoiceItems = [];
        let itemsToConfirm = [];
        let noStockQueue = [];
        // let shortfallMap = new Map(); 

        let choiceQueue = []; // items with allocations AND active exceptional
        for (const item of cleanedItems) {           
            const requested = Number(item.quantity) || 0;
            const pid = item.product.id || item.product;
            const allocation = allocationsByProduct[pid];
            const hasActiveExceptional = !!exceptionalByProduct[pid]?.active;
            if (!allocation || !allocation.batches?.length) {
                noStockQueue.push({ item, qty: item.quantity, activeExceptional: exceptionalByProduct[pid] || null });
                continue;
            }
            const totalAllocated = allocation.batches.reduce((s, b) => s + (Number(b.allocated_quantity) || 0), 0);
                if (allocation.batches.length === 1 && (totalAllocated === requested || allocation.fully_allocated)) {
            // Single-batch fully allocated
            if (hasActiveExceptional) {
                console.log("inside here 1");
                const singleBatchIsExceptional = (typeof allocation.batches[0].exceptional !== 'undefined')
                  ? !!allocation.batches[0].exceptional
                  : (exceptionalByProduct[pid]?.inventory_id
                     ? allocation.batches[0].inventory_id === exceptionalByProduct[pid].inventory_id
                     : false);

                if (singleBatchIsExceptional) {
                    console.log("inside here 2");
                    finalInvoiceItems.push({
                        ...item,
                        quantity: allocation.batches[0].allocated_quantity,
                        price: allocation.batches[0].selling_price,
                        batch_number: allocation.batches[0].batch_number,
                        expiry_date: allocation.batches[0].expiry_date,
                        price_list: allocation.batches[0].price_list_pk,
                        inventory: allocation.batches[0].inventory_id,
                        has_more_batches: allocation.has_more_batches || false,
                   });
                } else {
                    console.log("inside here 3");
                    choiceQueue.push({
                        type: 'single',
                        item,
                        singleLine: {
                            ...item,
                            quantity: allocation.batches[0].allocated_quantity,
                            price: allocation.batches[0].selling_price,
                            batch_number: allocation.batches[0].batch_number,
                            expiry_date: allocation.batches[0].expiry_date,
                            price_list: allocation.batches[0].price_list_pk,
                            inventory: allocation.batches[0].inventory_id,
                            has_more_batches: allocation.has_more_batches || false,
                        }
                    });
                }
            } else {
                console.log("inside here 4");
                finalInvoiceItems.push({
                    ...item,
                    quantity: allocation.batches[0].allocated_quantity,
                    price: allocation.batches[0].selling_price,
                    batch_number: allocation.batches[0].batch_number,
                    expiry_date: allocation.batches[0].expiry_date,
                    price_list: allocation.batches[0].price_list_pk,
                    inventory: allocation.batches[0].inventory_id,
                    has_more_batches: allocation.has_more_batches || false,
                });
            }
        } else {
            // Multi-batch
            console.log("inside here 5");
            console.log("exceptional: ", exceptionalByProduct);
            if (hasActiveExceptional) {
                console.log("inside here 6");
                const allExceptional = allBatchesMarkedExceptional(allocation.batches, exceptionalByProduct[pid]);
                const allocatedFromBatches = allocation.batches.reduce((s, b) => s + (Number(b.allocated_quantity) || 0), 0);

                if (allExceptional && allocatedFromBatches >= requested) {
                    console.log("inside here 7");
                    allocation.batches.forEach(b => {
                        finalInvoiceItems.push({
                            ...item,
                            quantity: b.allocated_quantity,
                            price: b.selling_price,
                            batch_number: b.batch_number,
                            expiry_date: b.expiry_date,
                            price_list: b.price_list_pk,
                            inventory: b.inventory_id,
                            has_more_batches: allocation.has_more_batches || false,
                        });
                    });
                } else {
                    console.log("inside here 8");
                    if (!allowExceptional) {
                        if (allocatedFromBatches < requested) {
                            console.log("inside here 10");
                            // Insufficient and exceptional disabled → stop processing this item and show error
                            handleApiError({ Error: `${item.product?.name || pid}: Insufficient stock (${allocatedFromBatches}/${requested}) and exceptional additions are disabled.` });
                            // skip adding anything for this item (stop further processing for it)
                        } else {
                            console.log("inside here 9");
                            allocation.batches.forEach(b => {
                                finalInvoiceItems.push({
                                    ...item,
                                    quantity: b.allocated_quantity,
                                    price: b.selling_price,
                                    batch_number: b.batch_number,
                                    expiry_date: b.expiry_date,
                                    price_list: b.price_list_pk,
                                    inventory: b.inventory_id,
                                    has_more_batches: allocation.has_more_batches || false,
                                });
                            });
                        }
                    } else {
                        console.log("inside here 11");
                        choiceQueue.push({ type: 'multi', item, allocations: allocation.batches, requested });
                    }
                }
            } else {
                console.log("inside here 12");
                itemsToConfirm.push({ item, allocations: allocation.batches });
            }
        }
        }

        // No stock at all for some items → ask to add inventory for them
        if (noStockQueue.length > 0) {
            console.log("inside here 13");
            // We already have exceptionalByProduct mapped by product id
            const withActive = [];
            const withoutActive = [];

            for (const entry of noStockQueue) {
                const pid = entry.item.product.id || entry.item.product;
                const ex  = exceptionalByProduct[pid];
                const enriched = { ...entry, activeExceptional: ex };
                if (ex?.active) withActive.push(enriched);
                else            withoutActive.push(enriched);
            }

            // 1) Immediately open dialog for items with an active exceptional
            if (withActive.length) {
                console.log("inside here 14");
                if (!allowExceptional) {
                    // If exceptional disabled, show error for each and stop their processing
                    withActive.forEach(w => {
                        const nm = w.item.product?.name || w.item.product;
                        handleApiError({ Error: `${nm}: No stock available and adding exceptional inventory is disabled by organization settings.` });
                    });
                } else {
                    console.log("inside here 15");
                    setConvertedInvoiceItems(finalInvoiceItems);
                    setTempFinalInvoiceItems(finalInvoiceItems);
                    setTempItemsToConfirm(itemsToConfirm);
                    if (choiceQueue && choiceQueue.length) {
                        console.log("inside here 16");
                        pendingChoiceQueueRef.current = choiceQueue;
                    }
                    setExceptionalQueue(withActive);
                    const first = withActive[0];
                    openExceptionalDialog('existing', first, first.activeExceptional);
                    if (withoutActive.length) {
                        console.log("inside here 17");
                        pendingOutOfStockWithoutActiveRef.current = withoutActive;
                    }
                    return;
                }
            }

            // 2) For items without an active exceptional, ask the user
            if (withoutActive.length) {
                console.log("inside here 18");
                const msg = [
                'Some items are out of stock:',
                ...withoutActive.map(x =>
                    `• ${(x.item.product?.name) || `#${x.item.product?.id || x.item.product}`} (${Number(x.qty) || 0})`
                ),
                '',
                'Do you have in-hand (old) stocks to add to inventory now?'
                ].join('\n');

                // If exceptional is globally disabled, do not prompt to add — show error and skip.
                    if (!allowExceptional) {
                        withoutActive.forEach(w => {
                            const nm = w.item.product?.name || w.item.product;
                            handleApiError({ Error: `${nm}: No stock available and exceptional inventory is disabled by organization settings.` });
                        });
                        // proceed to inline confirmation or final invoice using whatever we have
                        if (itemsToConfirm.length > 0) {
                            console.log("inside here 19");
                            setInlineBatchData(itemsToConfirm);
                           setInlineBatchAvailableItems(finalInvoiceItems);
                            setShowInlineBatchEditor(true);
                        } else {
                            console.log("inside here 20");
                            handleFinalInvoice(finalInvoiceItems);
                        }
                    } else {
                        console.log("inside here 21");
                        confirmWithNav({
                            header: 'No Stock',
                            message: msg,
                            icon: 'pi pi-exclamation-triangle',
                            acceptLabel: 'Add Inventory',
                            rejectLabel: 'Skip',
                            accept: () => {
                                setConvertedInvoiceItems(finalInvoiceItems);
                                setTempFinalInvoiceItems(finalInvoiceItems);
                                setTempItemsToConfirm(itemsToConfirm);
                                if (choiceQueue && choiceQueue.length) {
                                    pendingChoiceQueueRef.current = choiceQueue;
                                }
                                setExceptionalQueue(withoutActive);
                                const first = withoutActive[0];
                                const pid = first.item.product.id || first.item.product;
                                const ex = exceptionalByProduct[pid] || null;
                                openExceptionalDialog(ex?.active ? 'existing' : 'new', first, ex);
                            },
                           reject: () => {
                                if (choiceQueue && choiceQueue.length) {
                                    runChoiceQueue(choiceQueue, finalInvoiceItems, itemsToConfirm);
                                    return;
                                }
                                if (itemsToConfirm.length > 0) {
                                    setInlineBatchData(itemsToConfirm);
                                    setInlineBatchAvailableItems(finalInvoiceItems);
                                    setShowInlineBatchEditor(true);
                                } else {
                                    handleFinalInvoice(finalInvoiceItems);
                                }
                            },
                        });
                    }
            } else if (!withActive.length) {
                console.log("inside here 22");
                // Fallback if nothing to handle
                if (itemsToConfirm.length > 0) {
                    console.log("inside here 23");
                    setInlineBatchData(itemsToConfirm);
                    setInlineBatchAvailableItems(finalInvoiceItems);
                    setShowInlineBatchEditor(true);
                } else {
                    console.log("inside here 24");
                    handleFinalInvoice(finalInvoiceItems);
                }
            }
            return;
        }
        // --- Handle items with NO NEW stock and NO ACTIVE exceptional ---
        // Build a targeted list from what we just fetched/derived.
        const noStockWithoutActive = [];
        for (const item of cleanedItems) {
            const pid = item?.product?.id ?? item?.product;
            const allocation = allocationsByProduct[pid];
            const hasBatches = Array.isArray(allocation?.batches) && allocation.batches.length > 0;
            const ex = exceptionalByProduct[pid];
            if (!hasBatches && !(ex && ex.active)) {
                console.log("inside here 25");
                const req = Number(item.quantity || 0);
                if (req > 0) noStockWithoutActive.push({ item, qty: req });
            }
        }

        if (noStockWithoutActive.length > 0) {
            console.log("inside here 26");
            if (!allowExceptional) {
                // Org settings forbid exceptional additions: surface errors and proceed.
                noStockWithoutActive.forEach(w => {
                    const nm = w.item?.product?.name || (w.item?.product?.id ?? w.item?.product);
                    handleApiError({ Error: `${nm}: No stock available and exceptional inventory is disabled by organization settings.` });
                });
                // Fall through to choice queue / editor / finalize below.
            } else {
                console.log("inside here 27");
                // Ask the user whether to add exceptional for these items now.
                const msg = [
                    'Some items are out of stock:',
                    ...noStockWithoutActive.map(x =>
                        `• ${(x.item.product?.name) || `#${x.item.product?.id || x.item.product}`} (${Number(x.qty) || 0})`
                    ),
                    '',
                    'Do you want to add exceptional (OLD) inventory for these now?'
                ].join('\n');

                const res = await confirmAsync({
                    header: 'No Stock',
                    message: msg,
                    acceptLabel: 'Add Inventory',
                    rejectLabel: 'Skip',
                });

                if (res === 'accept') {
                    console.log("inside here 28");
                    // Seed snapshots so progress so far is preserved across dialogs
                    const seed = uniqueInvoiceLines([
                        ...(convertedInvoiceItemsRef.current || []),
                        ...(finalInvoiceItems || []),
                        ...(tempFinalInvoiceItems || []),
                    ]);
                    setConvertedInvoiceItems(seed);
                    convertedInvoiceItemsRef.current = seed;
                    setTempFinalInvoiceItems(seed);
                    setTempItemsToConfirm(Array.isArray(itemsToConfirm) ? itemsToConfirm : []);

                    // Park remaining NEW/OLD choices to resume after the exceptional dialog flow completes
                    if (choiceQueue && choiceQueue.length) {
                        console.log("inside here 29");
                        pendingChoiceQueueRef.current = choiceQueue;
                    }

                    // Start exceptional additions in "new" mode for these items
                    setExceptionalQueue(noStockWithoutActive);
                    const first = noStockWithoutActive[0];
                    openExceptionalDialog('new', first, null);
                    return; // resume after exceptional submit completes
                }
                // else: user chose Skip → continue to choice queue/editor/finalize
            }
        }
        if (choiceQueue.length > 0) {
            console.log("inside here 30");
            // Seed snapshots and run the centralized runner
            setTempFinalInvoiceItems(prev => uniqueInvoiceLines([...(prev || []), ...(finalInvoiceItems || [])]));
            setTempItemsToConfirm(prev => uniqueInvoiceLines([...(prev || []), ...(itemsToConfirm || [])]));
            setConvertedInvoiceItems(prev => {
              const next = uniqueInvoiceLines([...(prev || []), ...(finalInvoiceItems || [])]);
              convertedInvoiceItemsRef.current = next;
              return next;
            });
            await runChoiceQueue(choiceQueue, finalInvoiceItems, itemsToConfirm);
            return;
        }

    const combinedNoChoice = (convertedInvoiceItemsRef.current && convertedInvoiceItemsRef.current.length)
        ? convertedInvoiceItemsRef.current
        : finalInvoiceItems;
        console.log("combinedNoChoice: ",combinedNoChoice);
    const combinedNoChoiceUnique = uniqueInvoiceLines(combinedNoChoice);
    console.log("combinedNoChoiceUnique: ",combinedNoChoiceUnique);
    setConvertedInvoiceItems(combinedNoChoiceUnique);

    if (itemsToConfirm.length > 0) {
        console.log("inside here 31");
        setInlineBatchData(itemsToConfirm);
        setInlineBatchAvailableItems(combinedNoChoiceUnique);
        setShowInlineBatchEditor(true);
    } else {
        console.log("inside here 32");
        handleFinalInvoice(combinedNoChoiceUnique);
    }
    };

    const handleInlineBatchConfirm = (finalItems) => {
        // Close the editor, but DO NOT clear inlineBatchData yet — we need it below
        setShowInlineBatchEditor(false);

        // Merge with any prior ready items
        const merged = [...convertedInvoiceItems, ...finalItems];
        setConvertedInvoiceItems(merged);

        // --- Robust remainder detection ---
        // 1) Try to sum allocations per original rx item id if present on finalItems
        const byRxId = new Map();
        (finalItems || []).forEach(fi => {
            const rxId =
                fi.rx_item_id ?? fi.rxItemId ?? fi.rxId ?? fi.original_id ?? fi.rx_item ?? fi.item_id;
            if (rxId != null) {
                byRxId.set(rxId, (byRxId.get(rxId) || 0) + (Number(fi.quantity) || 0));
            }
        });

        // 2) Fallback: if we couldn't map by rx id at all, group by product id
        let byProductId = null;
        if (byRxId.size === 0) {
            byProductId = new Map();
            (finalItems || []).forEach(fi => {
                const pid = fi?.product?.id ?? fi.product;
                if (pid) {
                    byProductId.set(pid, (byProductId.get(pid) || 0) + (Number(fi.quantity) || 0));
                }
            });
        }

        // Build a queue of remainders that still need exceptional inventory
        const remainders = [];
        (inlineBatchData || []).forEach(({ item }) => {
            const requested = Number(item.quantity || 0);
            let allocated = 0;
            if (byRxId.size > 0) {
                // Prefer exact rx item id match
                allocated = byRxId.get(item.id) || 0;
            } else if (byProductId) {
                // Fallback by product id when rx id is not provided by the editor
                const pid = item?.product?.id ?? item.product;
                allocated = byProductId.get(pid) || 0;
            }
            const short = Math.max(0, requested - allocated);
            if (short > 0) remainders.push({ item, qty: short });
        });

        // Now it's safe to clear the editor caches
        setInlineBatchData([]);
        setInlineBatchAvailableItems([]);

        // Only prompt exceptional for items that already have an ACTIVE exceptional batch
        const remaindersWithActive = (remainders || []).filter(r => {
            const pid = r.item?.product?.id ?? r.item?.product;
            const ex  = exceptionalByProduct[pid];
            return !!(ex && ex.active);
        });

        if (remaindersWithActive.length > 0) {
            // setTempItemsToConfirm([]);
            setExceptionalQueue(remaindersWithActive);
            const first = remaindersWithActive[0];
            const pid = first.item.product.id || first.item.product;
            const ex  = exceptionalByProduct[pid];
            openExceptionalDialog('existing', first, ex);
            return;
        }

        // No remainders — available batches satisfied the request; proceed to finalize
        handleFinalInvoice(uniqueInvoiceLines(merged));
    };

  const handleExceptionalSubmit = async () => {
    const f = exceptionalForm;
    if (!f.productId || !f.qty || (f.productObj?.is_expiring && !f.expiry) || !f.selling_price) {
      handleApiError({ Error: "Please fill quantity, expiry, and selling price." });
      return;
    }
    try {
        let useExisting = !!f.useExisting;
        let deactivateExisting = !!f.deactivateExisting;

        // helper to actually submit with chosen flags and then advance the queue / finalize
        const submitWith = async (useExistingChoice, deactivateExistingChoice) => {
            const payload = {
                product_id: f.productId,
                quantity: Number(f.qty),
                expiry_date: f.expiry ? format(f.expiry, 'yyyy-MM-dd') : null,
                purchase_price: f.purchase_price ?? 0,
                selling_price: Number(f.selling_price),
                use_existing: !!useExistingChoice,
                deactivate_existing: !!deactivateExistingChoice,
            };
            if (f.batch_number) payload.batch_number = f.batch_number;

            const res = await axiosInstance.post('/api/inventory/exceptional/create-or-topup/', payload);
            const r = res.data || {};

            const price = r.selling_price ?? f.selling_price ?? 0;
            const newItem = {
              ...exceptionalForm.productObj,
            };

            // Build the new line and an updated list
            const newLine = {
                id: Date.now() + Math.random(),
                product: newItem,
                product_id: f.productId,
                rx_item_id: f.itemId,
                quantity: Number(r.quantity),
                price,
                batch_number: r.batch_number || f.batch_number || '',
                expiry_date: r.expiry_date || (f.expiry ? format(f.expiry, 'yyyy-MM-dd') : null),
                price_list: r.price_list_pk ?? f.price_list ?? undefined,
                inventory: r.inventory_id ?? undefined,
            };

            // Merge with any prior ready items (sum quantities on identical key)
            const updatedList = (() => {
                const prev = convertedInvoiceItems || [];

                // normalize helpers
                const normPid = (p) => String((p && p.id != null) ? p.id : p ?? '');
                const normBatch = (b) => String(b ?? '').trim().toUpperCase();

                const pid2 = normPid(newLine?.product);
                const bn2  = normBatch(newLine?.batch_number);

                // don’t merge if batch number isn’t set yet (placeholder/new)
                if (!bn2) return [...prev, newLine];

                const idx = prev.findIndex(li => {
                    const pid = normPid(li?.product);
                    const bn  = normBatch(li?.batch_number);
                    return pid === pid2 && bn === bn2;
                });

                if (idx >= 0) {
                    const merged = [...prev];
                    const base   = merged[idx];
                    const q      = (Number(base.quantity) || 0) + (Number(newLine.quantity) || 0);

                    // keep existing price (assuming same batch ⇒ same price); recompute total
                    const price  = Number(base.price ?? newLine.price ?? 0);
                    merged[idx]  = { ...base, quantity: q, total: q * price };

                    return merged;
                }
                return [...prev, newLine];
            })();
            console.log("convertedInvoiceItems: ", convertedInvoiceItems);
            setConvertedInvoiceItems(updatedList);
            console.log("Newline: ", newLine);
            console.log("updated list: ", updatedList);

            // Advance the queue
            const nextQ = exceptionalQueue.slice(1);
            setExceptionalQueue(nextQ);
            if (nextQ.length > 0) {
                console.log("Now again here 1");
              const next = nextQ[0];
              const pid = next.item.product.id || next.item.product;
              const ex  = (exceptionalByProductRef.current || {})[pid];
              openExceptionalDialog(ex?.active ? 'existing' : 'new', next, ex);
            } else {
                console.log("Now again here 2");
              setExceptionalVisible(false);
              // Finalize with the concrete, up-to-date list
              const rest = pendingChoiceQueueRef.current || [];
              if (rest.length > 0) {
                console.log("Now again here 3");
                  // Resume centrally with the same runner
                  const restCopy = [...rest];
                  pendingChoiceQueueRef.current = [];
                  await runChoiceQueue(restCopy, tempFinalInvoiceItems, tempItemsToConfirm);
              } else {
                console.log("Now again here 4");
                 // --- RESUME ANY DEFERRED "NO STOCK (NO ACTIVE EXCEPTIONAL)" ITEMS ---
                if (pendingOutOfStockWithoutActiveRef.current && pendingOutOfStockWithoutActiveRef.current.length) {
                    console.log("Now again here 5");
                    const withoutActive = pendingOutOfStockWithoutActiveRef.current;
                    pendingOutOfStockWithoutActiveRef.current = null;

                    // Seed with everything staged so far (UI list, cached list, and the just-updated list)
                    const seed = uniqueInvoiceLines([
                        ...(updatedList || []),
                        ...(convertedInvoiceItemsRef.current || []),
                        ...(tempFinalInvoiceItems || []),
                    ]);
                    setConvertedInvoiceItems(seed);
                    setTempFinalInvoiceItems(seed);

                    // Build confirm message
                    const msg = [
                        'Some items are out of stock:',
                        ...withoutActive.map(x =>
                            `• ${(x.item.product?.name) || `#${x.item.product?.id || x.item.product}`} (${Number(x.qty) || 0})`
                        ),
                        '',
                        'Do you have in-hand (old) stocks to add to inventory now?'
                    ].join('\n');

                    confirmWithNav({
                        header: 'No Stock',
                        message: msg,
                        icon: 'pi pi-exclamation-triangle',
                        acceptLabel: 'Add Inventory',
                        rejectLabel: 'Skip',
                        accept: () => {
                           setTempItemsToConfirm(Array.isArray(tempItemsToConfirm) ? tempItemsToConfirm : []);
                            setExceptionalQueue(withoutActive);
                            const first = withoutActive[0];
                            const pid = first.item.product.id || first.item.product;
                            const ex  = exceptionalByProduct[pid] || null;
                            openExceptionalDialog(ex?.active ? 'existing' : 'new', first, ex);
                        },
                        reject: () => {
                            if (Array.isArray(tempItemsToConfirm) && tempItemsToConfirm.length > 0) {
                                setInlineBatchData(tempItemsToConfirm);
                                setInlineBatchAvailableItems(seed);
                                setShowInlineBatchEditor(true);
                            } else {
                                handleFinalInvoice(seed);
                            }
                        },
                    });
                    return;
                }

                // No deferred "withoutActive" items — proceed with editor/finalize on the merged list
                console.log("convertedInvoiceItemsRef: ", convertedInvoiceItemsRef.current);
                console.log("tempFinalInvoiceItems: ", tempFinalInvoiceItems);
                console.log("updatedList_last: ", updatedList);
                const seed = uniqueInvoiceLines([
                    ...(updatedList || []),
                    ...(convertedInvoiceItemsRef.current || []),
                    ...(tempFinalInvoiceItems || []),
                ]);
                setConvertedInvoiceItems(seed); 
                convertedInvoiceItemsRef.current = seed;
                if (Array.isArray(tempItemsToConfirm) && tempItemsToConfirm.length > 0) {
                    console.log("Now again here 6");
                    setInlineBatchData(tempItemsToConfirm);
                    setInlineBatchAvailableItems(seed);
                    setShowInlineBatchEditor(true);
                } else {
                    console.log("Now again here 7");
                    handleFinalInvoice(seed);
                }
              }
            }
            handleApiSuccess("Inventory added for old stocks.");
        };

        if (exceptionalActiveInfo && exceptionalActiveInfo.inventory_id && useExisting) {
            console.log("Now again here 8");
            const oldExpiry = exceptionalActiveInfo.expiry_date ? exceptionalActiveInfo.expiry_date.split('T')[0] : null;
            const newExpiry = f.expiry ? format(f.expiry, 'yyyy-MM-dd') : null;
            const oldSelling = exceptionalActiveInfo.selling_price != null ? Number(exceptionalActiveInfo.selling_price) : null;
            const oldPurchase = exceptionalActiveInfo.purchase_price != null ? Number(exceptionalActiveInfo.purchase_price) : null;
            const oldBatch = exceptionalActiveInfo.batch_number || '';
            const changed = (
                oldExpiry !== newExpiry
                || oldSelling !== Number(f.selling_price)
                || oldPurchase !== Number(f.purchase_price)
                || (f.batch_number && f.batch_number !== oldBatch)
            );
            if (changed) {
                const nm = f.productName || `#${f.productId}`;
                
                confirmDialog({
                    header: `Update Exceptional Batch — ${nm}`,
                    message: `You changed expiry/price/batch details for ${nm}.\nDo you want to update the existing batch (top-up) or create a NEW exceptional batch (old one will be deactivated)?`,
                    acceptLabel: "Create NEW batch",
                    rejectLabel: "Update existing",
                    accept: async () => {
                        try {
                            await submitWith(false, true);
                        } catch (e) {
                            handleApiError(e);
                        }
                    },
                    reject: async () => {
                        try {
                            await submitWith(true, false);
                        } catch (e) {
                            handleApiError(e);
                        }
                    },
                });
                return; // stop normal flow, wait for user choice
                
            }
        }
        await submitWith(useExisting, deactivateExisting);
    } catch (err) {
      handleApiError(err);
    }
  };
  
  const doSaveAndConvert = async () => {
    const payload = {
        ...prescription,
        customer: prescription.customer.id,
        date: prescription.date
            ? format(new Date(prescription.date), 'yyyy-MM-dd')
            : null,
        items: prescription.items.map((item) => ({
            ...item,
            product_id: item.product.id,
        })),
        portal_prescription_upload_id: portalPrescriptionUploadId.current || undefined,
    };

    const result = await axiosInstance.post('/api/prescriptions/', payload);

    setPrescriptions((prev) => [...prev, result.data]);
    pendingPrescriptionId.current = result.data.id;
    setPrescription(result.data);
    setIsReadOnly(true);
    setIsRevising(false);
    setDialogName(`Prescription Details: ${result.data.id}`);

    handleConvertClick();
  };

  const handleFinalInvoice = async (invoiceItems) => {
    try {
        // 1) Normalize/flatten whatever comes in (array, nested arrays, etc.)
        console.log("invoice: ",invoiceItems);
        const flat = Array.isArray(invoiceItems)
        ? (invoiceItems.flat ? invoiceItems.flat() : [].concat(...invoiceItems))
        : [];
        console.log("Flat invoice: ",flat);
        const flatUnique = combineInvoiceLines(flat);

        // Build from the unique list instead of potentially duplicated 'flat'
        const itemsUnique = flatUnique.map((item) => ({
            product: item?.product?.id ?? item.product,
            quantity: Number(item.quantity) || 0,
            price: item.price != null ? Number(item.price) : undefined,
            batch_number: item.batch_number ?? undefined,
            expiry_date: item.expiry_date ?? undefined,
            price_list: item.price_list ?? undefined,
            inventory: item.inventory ?? undefined,
            rx_item_id: item.rx_item_id ?? item.id ?? undefined,
        })).filter(x => x.product && x.quantity > 0);
        const op = verifiedOperatorRef.current || verifiedOperator;
        console.log("verified op: ", op);
        const payload = {
        prescription_id: prescription.id || pendingPrescriptionId.current,
        operator_id: op?.operator_id || null,
        operator_verification_method: op?.verification_method || null,
        portal_refill_request: portalRefillRequestId.current,
        portal_prescription_upload: portalPrescriptionUploadId.current,
        items: itemsUnique,
        
        };

        // Helpful debug while test:
        // console.log('[RX→INV] sending items:', items);

        const response = await axiosInstance.post('/api/convert-prescription-to-invoice/', payload);
        if (response.data.id) {

            handleApiSuccess("Prescription converted to invoice!");

            if (portalRefillRequestId.current) {
                try {
                    await axiosInstance.patch(
                        `/api/refill-requests/${portalRefillRequestId.current}/`,
                        { status: "processing" }
                    );
                } catch (err) {
                    console.error("Failed to update refill request status", err);
                }
            }

            navigate(`/invoices?invoiceId=${response.data.id}`);
        }
    } catch (error) {
        handleApiError(error);
    }
  };


    const handleBatchDialogConfirm = (confirmedSplits) => {
        const splitInvoiceItems = confirmedSplits
            .filter(b => b.allocated_quantity > 0)
            .map(b => ({
                ...pendingBatchDialog.item,
                rx_item_id: pendingBatchDialog?.item?.id,
                quantity: b.allocated_quantity,
                price: b.selling_price,
                batch_number: b.batch_number,
                expiry_date: b.expiry_date,
                price_list: b.price_list_pk,
                inventory: b.inventory_id,
            }));

        const rxId = pendingBatchDialog?.item?.id;
        // Build a quick set of the new split keys for exact-dup filtering
        const newKeys = new Set(
            splitInvoiceItems.map(si => {
                const pid = si?.product?.id ?? si.product;
                const bn  = si?.batch_number ?? '';
                const inv = si?.inventory ?? '';
                return `${pid}|${bn}|${inv}`;
            })
        );
        const base = (convertedInvoiceItems || []).filter(li => {
            // Drop any lines that belong to this same RX item being split
            const liRx = li?.rx_item_id ?? li?.id;
            if (rxId != null && liRx === rxId) return false;

            // Also drop any exact duplicate that matches one of the new split rows
            const pid = li?.product?.id ?? li.product;
            const bn  = li?.batch_number ?? '';
            const inv = li?.inventory ?? '';
            const key = `${pid}|${bn}|${inv}`;
            return !newKeys.has(key);
        });

        const updatedItems = [...base, ...splitInvoiceItems];

        if (pendingBatchDialog.rest.length > 0) {
            const { item, allocations } = pendingBatchDialog.rest[0];
            setConvertedInvoiceItems(updatedItems);
            setPendingBatchAllocations(allocations);
            setPendingBatchDialog({ item, rest: pendingBatchDialog.rest.slice(1) });
        } else {
            setConvertedInvoiceItems(updatedItems);
            setPendingBatchAllocations([]);
            setPendingBatchDialog(null);
            handleFinalInvoice(uniqueInvoiceLines(updatedItems));
        }
    };

    // === Cancel handler for Exceptional dialog ===
  const handleExceptionalCancel = async () => {
        setExceptionalVisible(false);

        // Identify current (front) exceptional item, if any
        const current = exceptionalQueue && exceptionalQueue.length ? exceptionalQueue[0] : null;
        const rxId = current?.item?.id ?? null;

    // Remove any placeholder lines for this RX item (those without inventory/batch)
    if (rxId != null) {
        setConvertedInvoiceItems(prev =>
            (prev || []).filter(li => {
                const sameRx = (li?.rx_item_id ?? li?.id) === rxId;
                const placeholder = !li?.inventory && !li?.batch_number;
                return !(sameRx && placeholder);
            })
        );
        setTempFinalInvoiceItems(prev =>
            (prev || []).filter(li => {
                const sameRx = (li?.rx_item_id ?? li?.id) === rxId;
                const placeholder = !li?.inventory && !li?.batch_number;
                return !(sameRx && placeholder);
            })
        );
    }

    // Drop the cancelled item from the exceptional queue
    const nextQ = (exceptionalQueue || []).slice(1);
    setExceptionalQueue(nextQ);
    // If more exceptional items remain, open the next one
    if (nextQ.length > 0) {
      const next = nextQ[0];
      const pid = next.item?.product?.id ?? next.item?.product;
      const ex  = (exceptionalByProductRef.current || {})[pid];
      openExceptionalDialog(ex?.active ? 'existing' : 'new', next, ex);
      return;
    }

    // Otherwise resume any pending NEW/OLD choice items
    const restChoices = pendingChoiceQueueRef.current || [];
    if (restChoices.length > 0) {
      const restCopy = [...restChoices];
      pendingChoiceQueueRef.current = [];
      await runChoiceQueue(restCopy, tempFinalInvoiceItems, tempItemsToConfirm);
      return;
    }

    // Or resume the deferred "no stock without active exceptional" prompt, if any
    if (pendingOutOfStockWithoutActiveRef.current && pendingOutOfStockWithoutActiveRef.current.length) {
      const withoutActive = pendingOutOfStockWithoutActiveRef.current;
      pendingOutOfStockWithoutActiveRef.current = null;

      const seed = uniqueInvoiceLines([
        ...(convertedInvoiceItemsRef.current || []),
        ...(tempFinalInvoiceItems || []),
      ]);
      setConvertedInvoiceItems(seed);
      setTempFinalInvoiceItems(seed);

      const msg = [
        'Some items are out of stock:',
        ...withoutActive.map(x =>
          `• ${(x.item.product?.name) || `#${x.item.product?.id || x.item.product}`} (${Number(x.qty) || 0})`
        ),
        '',
        'Do you have in-hand (old) stocks to add to inventory now?'
      ].join('\n');

      confirmWithNav({
        header: 'No Stock',
        message: msg,
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Add Inventory',
        rejectLabel: 'Skip',
        accept: () => {
          setTempItemsToConfirm(Array.isArray(tempItemsToConfirm) ? tempItemsToConfirm : []);
          setExceptionalQueue(withoutActive);
          const first = withoutActive[0];
          const pid = first.item?.product?.id ?? first.item?.product;
          const ex  = exceptionalByProduct[pid] || null;
          openExceptionalDialog(ex?.active ? 'existing' : 'new', first, ex);
        },
        reject: () => {
          if (Array.isArray(tempItemsToConfirm) && tempItemsToConfirm.length > 0) {
            setInlineBatchData(tempItemsToConfirm);
            setInlineBatchAvailableItems(seed);
            setShowInlineBatchEditor(true);
          } else {
            handleFinalInvoice(seed);
          }
        },
      });
      return;
    }

    // Nothing else to do — proceed with editor/finalize using what we have
    const seed = uniqueInvoiceLines([
      ...(convertedInvoiceItemsRef.current || []),
      ...(tempFinalInvoiceItems || []),
    ]);
    setConvertedInvoiceItems(seed);
    if (Array.isArray(tempItemsToConfirm) && tempItemsToConfirm.length > 0) {
      setInlineBatchData(tempItemsToConfirm);
      setInlineBatchAvailableItems(seed);
      setShowInlineBatchEditor(true);
    } else {
      handleFinalInvoice(seed);
    }
  };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const actionType = e.nativeEvent.submitter?.name;

        // Same "Save" button, two meanings: while viewing an EXISTING
        // prescription, it persists just quantity/duration/the refill flag
        // (see handleSaveFillDetails) rather than the full create/revise
        // payload below — one button rather than a second "Save" that's
        // always disabled the other way around.
        if (actionType === "save" && isReadOnly) {
            await handleSaveFillDetails();
            return;
        }

        const payload = {
            ...prescription,
            customer: prescription.customer.id,
            date: prescription.date ? format(new Date(prescription.date), 'yyyy-MM-dd') : null,
            items: prescription.items.map((item) => ({
                ...item,
                product_id: item.product.id,
            })),
            portal_prescription_upload_id: portalPrescriptionUploadId.current || undefined,
        };
        try {
            if (actionType === "save") {
            if (isRevising) {
                // Call PUT API for revision
                const result = await axiosInstance.put(`/api/prescriptions/${prescription.id}/`, payload);
                setPrescriptions((prev) =>
                    prev.map((p) => (p.id === prescription.id ? result.data : p))
                );
                handleApiSuccess('Prescription revised successfully!');
            } else {
                // Call POST API for new prescription
                const result = await axiosInstance.post('/api/prescriptions/', payload);
                setPrescriptions([...prescriptions, result.data]);
                handleApiSuccess('Prescription created successfully!');
            }
            setPrescription({
                customer: null,
                doctor_name: '',
                duration: null,
                date: new Date(),
                items: [{ product: null, quantity: '', dosage: '1', frequency: '', duration: '' }],
            });
            setDialogVisible(false);
            setIsRevising(false);

        } else if (actionType === "saveAndConvert") {
            if (operatorAuthEnabled && !verifiedOperatorRef.current) {
                setPendingConvertAction(() => doSaveAndConvert);
                setShowOperatorAuth(true);
                return;
            }

            await doSaveAndConvert();
        }

        } catch (error) {
            handleApiError(error);
        }
    };

    const removeItem = (itemId) => {
        setPrescription((prev) => ({
            ...prev,
            items: prev.items.filter(item => item.id !== itemId),
        }));
    };

    const handleDurationChange = (value) => {
        setPrescription((prev) => {
            const updatedItems = prev.items.map((item) => ({
                ...item,
                duration: (item.duration === null || item.duration === prev.duration) ? value : item.duration, 
            }));
    
            return { ...prev, duration: value, items: updatedItems };
        });
    };
    
    const handleRowClick = async  (rowData) => {
        try {
            // Fetch the full prescription details (including customer and product details)
            const response = await axiosInstance.get(`/api/prescriptions/${rowData.id}/`);
            const detailedPrescription = response.data;

            // Same clinic_source_name-driven detection as loadPrescriptionFromNav
            // above — a row opened directly from this list (rather than via the
            // Accept/View-Prescription navigation) still needs the quantity-entry
            // gate reapplied if it came from a clinic and still has unset items.
            if (detailedPrescription.clinic_source_name) {
                detailedPrescription.items = detailedPrescription.items.map(item => ({
                    ...item,
                    quantity: Number(item.quantity) > 0 ? item.quantity : null,
                }));
                setIsClinicMode(true);
                setClinicSourceCapture(detailedPrescription.clinic_source_name);
            }

            // Set the detailed prescription data to state
            setPrescription(detailedPrescription);
            // Store upload data for the toolbar button — panel opens inside the dialog
            if (detailedPrescription.portal_prescription_upload?.file_url) {
                setPrescriptionUploadData(detailedPrescription.portal_prescription_upload);
            } else {
                setPrescriptionUploadData(null);
            }
            setIsReadOnly(true); // Enable read-only mode
            setDialogVisible(true);
            setDialogName(`Prescription Details: ${rowData.id}`);
        } catch (error) {
            handleApiError(error);
        }
    };

    const columns = [
        {   
            Header: 'Id#', accessor: 'id',  
            bodyTemplate: (rowData) => (
                <a
                    href="/prescriptions/#"
                    style={{ textDecoration: 'none' }}
                    onClick={(e) => {
                        beginDrilldown();
                        e.preventDefault();
                        handleRowClick(rowData);
                        push({
                            label: `Prescription: ${rowData.id}`,
                        });
                    }}
                >
                    {rowData.id}
                </a>
            ),
        },
        { 
            Header: 'Customer', 
            accessor: 'customer_name', 
            sortable: true, 
            filtered: true,
            bodyTemplate: (row) => (
            <span
                style={{ color: '#2563eb', cursor: 'pointer' }}
                onClick={() =>{
                    beginDrilldown();
                    navigate(`/customers/${row.customer}`)
                    push({
                        label: `Prescriptions`,
                        path: '/prescriptions',
                    });
                    push({
                        label: `Customer: ${row.customer_name}`,
                        path: `/customers/${row.customer}`,
                    });
                }}
            >
                {row.customer_name}
            </span>
            )
        },
        { Header: 'Date', accessor: 'date', sortable: true, filtered: true },
        { Header: 'Doctor', accessor: 'doctor_name', sortable: true, filtered: true },
        { Header: 'Version', accessor: 'version', sortable: true },
    ];

    const renderHeader = () => (
        <div className="flex justify-content-between align-items-center gap-4 md:gap-2" style={{marginRight:'50px'}}>
            <h5 className="text-base sm:text-lg lg:text-xl">Prescription Items:</h5>
                <div className="flex flex-wrap align-items-center gap-2 sm:gap-2">
                    <Button type="button" icon="pi pi-plus" onClick={(e) => {e.preventDefault(); addNewItem(); }} disabled={isReadOnly || !canManagePrescriptions} className="p-mt-2" style={{borderRadius:"6px"}} />
                </div>
        </div>
    );
    const header = renderHeader();

    const cancelHandler = (e) => {
        setDialogVisible(false);
        setPrescriptionUploadData(null);
        navigate(location.pathname, { replace: true, state: {} });
        setIsRefillMode(false);
        setRefillDaysCapture(null);
        setIsClinicMode(false);
        setClinicSourceCapture(null);
        setIsRevising(false);
        setIsReadOnly(false);
        setDialogName('Create Prescription');
        e.preventDefault(); 
        pop();
        setPrescription({
            customer: null,
            doctor_name: '',
            duration: null,
            date: new Date(),
            items: [{ product: null, quantity: '', dosage: '1', frequency: '', duration: '' }],
        });
    }

    const headerAction = canManagePrescriptions ? (
        <Button 
            type="button" 
            icon="pi pi-plus" 
            label="Add Prescription" 
            outlined onClick={() => {
                setDialogVisible(true);
                setIsRevising(false);
                setPrescription({
                    customer: null,
                    doctor_name: '',
                    duration: null,
                    date: new Date(),
                    items: [{ product: null, quantity: '', dosage: '1', frequency: '', duration: '' }],
                });
                setIsReadOnly(false);
            }} 
            className="w-full sm:w-auto" 
        />
    ) : null;

    return (
        <UploadFilePanel uploadData={prescriptionUploadData}>
        <div>
            <div className="containers">
                <GlobalTable
                    columns={columns}
                    data={prescriptions}
                    headerAction={headerAction}
                    tabletitle="Prescription List"
                    loading={loading}
                    showEditColumn={false}
                    lazy
                    totalRecords={totalRecords}
                    lazyParams={lazyParams}
                    onPage={onPage}
                    onSort={onSort}
                    onFilter={onFilter}
                />
            </div>
            <PrescriptionDialog
                visible={dialogVisible}
                onHide={() => { setDialogVisible(false); setPrescriptionUploadData(null); }}
                prescription={prescription}
                handleItemChange={handleItemChange}
                handleSubmit={handleSubmit}
                handleSearchCustomer={handleSearchCustomer}
                handleDurationChange={handleDurationChange}
                handleRevise={handleRevise}
                handleConvertClick={handleConvertClick}
                handleCustomerDialogVisible={handleCustomerDialogVisible}
                setPrescription={setPrescription}
                removeItem={removeItem}
                isReadOnly={isReadOnly}
                isRevising={isRevising}
                dialogName={dialogName}
                filteredCustomers={filteredCustomers}
                header={header}
                frequency={frequency}
                cancelhandler={cancelHandler}
                prodInputRefs={prodInputRefs}
                addNewItem={addNewItem}
                isRefillRequest={isRefillMode || isClinicMode}
                refillRequestDays={refillDaysCapture}
                clinicSourceName={isClinicMode ? clinicSourceCapture : null}
                fromUpload={!!location.state?.fromUploadId}
                canManagePrescriptions={canManagePrescriptions}
                canManageCustomers={canManageCustomers}
                canCreateInvoice={canManageInvoice}
                portalUploadData={prescriptionUploadData}
            />
            {pendingBatchDialog && (
                <BatchAllocationDialog
                    visible={!!pendingBatchDialog}
                    allocations={pendingBatchAllocations}
                    totalQty={pendingBatchDialog.item.quantity}
                    onAllocationChange={(rowIndex, value) => {
                        setPendingBatchAllocations(prev =>
                            prev.map((b, idx) =>
                                idx === rowIndex ? { ...b, allocated_quantity: value } : b
                            )
                        );
                    }}
                    onConfirm={() => handleBatchDialogConfirm(pendingBatchAllocations)}
                    onHide={() => setPendingBatchDialog(null)}
                />
            )}
            <CustomerDialog
                visible={isCustomerDialogVisible && canManageCustomers}
                onHide={() => setCustomerDialogVisible(false)}
                customer={customer}
                handleChange={handleCusChange}
                handleSubmit={handleCusSubmit}
                isReadOnly={false}
                dialogName={'Create Customer'}
                isRequired={true}
                titles={titles}
                categories={categories}
                segments={segments}
                priority={priority}
                status={status}
            />
            <InlineBatchEditorDialog
                visible={showInlineBatchEditor}
                items={inlineBatchData}
                availableItems={inlineBatchAvailableItems}
                onHide={() => setShowInlineBatchEditor(false)}
                onConfirm={handleInlineBatchConfirm}
            />
            <Dialog
                header="Exceptional Inventory"
                visible={exceptionalVisible}
                onHide={handleExceptionalCancel}
                modal
                style={{ width: '32rem', borderRadius: '12px' }}
                onShow={() => {
                    if (exceptionalForm?.useExisting) {
                        setTimeout(() => exAddBtnRef.current?.focus(), 0);
                    } else {
                        setTimeout(() => exExpiryRef.current?.input?.focus?.(), 0);
                    }
                }}
            >
                <div className="p-fluid formgrid grid">
                    <div className="field col-12">
                    <label className="font-bold">Product</label>
                    <div>{exceptionalForm.productObj?.name || exceptionalForm.productObj?.label}</div>
                    </div>

                    <div className="field col-6">
                    <label className="font-bold">Quantity</label>
                    <InputNumber
                        value={exceptionalForm.qty}
                        onValueChange={(e) => setExceptionalForm((p) => ({ ...p, qty: e.value || 0 }))}
                        inputClassName="w-full"
                        placeholder="Qty"
                        min={1}
                    />
                    </div>

                    <div className="field col-6">
                    <label className="font-bold">Expiry Date</label>
                    <Calendar
                        ref={exExpiryRef}
                        value={exceptionalForm.expiry}
                        disabled={!exceptionalForm.productObj?.is_expiring}
                        onChange={(e) => setExceptionalForm((p) => ({ ...p, expiry: e.value }))}
                        onSelect={() => {setTimeout(() => exPurchaseRef.current?.focus?.(), 0);}}
                        dateFormat="yy-mm-dd"
                        showIcon
                        placeholder="YYYY-MM-DD"
                        className="w-full"
                    />
                    </div>

                    {exceptionalForm.useExisting && (
                    <div className="field col-12">
                        <label className="font-bold">Batch Number (existing)</label>
                        <InputText value={exceptionalForm.batch_number || ''} readOnly className="w-full" />
                    </div>
                    )}

                    <div className="field col-6">
                    <label className="font-bold">Purchase Price (optional)</label>
                    <InputNumber
                        ref={exPurchaseRef}
                        value={exceptionalForm.purchase_price}
                        onValueChange={(e) => setExceptionalForm((p) => ({ ...p, purchase_price: e.value ?? 0 }))}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                exSellingRef.current?.focus?.();
                            }
                        }}
                        inputClassName="w-full"
                        min={0}
                        placeholder="0"
                    />
                    </div>

                    <div className="field col-6">
                    <label className="font-bold">Selling Price</label>
                    <InputNumber
                        ref={exSellingRef}
                        value={exceptionalForm.selling_price}
                        onValueChange={(e) => setExceptionalForm((p) => ({ ...p, selling_price: e.value || 0 }))}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                exAddBtnRef.current?.focus?.();
                            }
                        }}
                        inputClassName="w-full"
                        min={0}
                        placeholder="Required"
                    />
                    </div>
                </div>

                <div className="flex justify-content-end gap-2 mt-3">
                    <Button ref={exAddBtnRef} label="Add & Continue" onClick={handleExceptionalSubmit} icon="pi pi-check" />
                    <Button label="Cancel" outlined onClick={handleExceptionalCancel} />
                </div>
            </Dialog>
             <OperatorAuthDialog
                visible={showOperatorAuth}
                orgSettings={JSON.parse(localStorage.getItem('organizationSettings') || '{}')}
                onSuccess={(operator) => {
                    verifiedOperatorRef.current = operator;
                    setVerifiedOperator(operator)
                    setShowOperatorAuth(false);
                    if (pendingConvertAction) {
                        const action = pendingConvertAction;
                        setPendingConvertAction(null);
                        action();
                    }
                }}
                onCancel={() => {
                    setShowOperatorAuth(false);
                    setDialogVisible(false);
                }}
                onHide={() => {
                    setShowOperatorAuth(false);
                    setPendingConvertAction(null);
                }}
            />
        </div>
    </UploadFilePanel>
    );
};

export default Prescriptions;